
import Vue, { PropType } from 'vue';
import { cloneDeep, DebouncedFunc, get, throttle } from 'lodash';
import { Unsubscriber } from '@/core/event-bus';
import Api from '@/core/api';
import { PlayerState } from '@/core/player-state';
import addEventListener from '@/common/add-event-listener';

interface SubtitlesConfig {
    position?: 'top' | 'bottom';
}

interface FontStyles {
    minWidth: number;
    style: Styles
}

interface Styles {
    fontSize: number,
    lineHeight: number,
    maxWidth?: string,
    marginLeft?: string
}

interface DynamicFontStyles {
    minWidth: number;
    style: DynamicStyles
}

interface DynamicStyles {
    fontSize: (width:number) => number,
    lineHeight: (width:number) => number,
    maxWidth?: string,
    marginLeft?: string
}

const FONTS_FOR_SCREEN_WIDTH: FontStyles[] = [
    {
        minWidth: 1920,
        style: {
            fontSize: 40,
            lineHeight: 62,
        }
    }, {
        minWidth: 1440,
        style: {
            fontSize: 30,
            lineHeight: 45,
        }
    }, {
        minWidth: 1024,
        style: {
            fontSize: 25,
            lineHeight: 40,
        }
    }, {
        minWidth: 560,
        style: {
            fontSize: 17,
            lineHeight: 27,
        }
    }, {
        minWidth: 375,
        style: {
            fontSize: 14,
            lineHeight: 22,
        }
    }, {
        minWidth: 0,
        style: {
            fontSize: 0,
            lineHeight: 0,
        }
    }
];

export default Vue.extend({
    props: {
        api: {
            type: Object as PropType<Api>,
            required: true,
        },
        config: {
            type: Object as PropType<SubtitlesConfig>,
            default: () => ({} as PropType<SubtitlesConfig>)
        }
    },

    data() {
        return {
            rawSubtitles: [] as string[],
            oldSubtitles: [] as string[],
            newSubtitles: [] as string[],
            subtitlesEnabled: false as boolean,
            unsubscribers: [] as Unsubscriber[],
            resizeListener: (() => undefined) as unknown as DebouncedFunc<()=>void>,
            fontStyles: {},
            styles: [] as DynamicFontStyles[]
        };
    },

    computed: {
        subtitleVisible(): boolean {
            return this.subtitlesEnabled && (!!this.newSubtitles?.length || !!this.oldSubtitles?.length);
        },

        subtitlePosition(): 'top' | 'bottom' {
            return (this.config as SubtitlesConfig)?.position || 'bottom';
        }
    },

    watch: {
        rawSubtitles: {
            immediate: true,
            handler(newVal, oldVal) {
                // TODO: find the cause of the race condition and remove this watcher
                // we had an event where the resizeListener was called before the proper rendering of the parent component
                // resulting in font-size being 0, rechecking required size here to be sure
                this.resizeListener();

                let newCaptions = [] as string[];
                let oldCaptions = [] as string[];
                for (const s1 of newVal) {
                    (oldVal.filter((s2: string) => s1 === s2).length ? oldCaptions : newCaptions).push(s1);
                }
                newCaptions = splitLinesAtLineBreak(newCaptions);
                oldCaptions = splitLinesAtLineBreak(oldCaptions);

                this.oldSubtitles = oldCaptions;
                this.newSubtitles = newCaptions;
            }
        },
        subtitlesEnabled: {
            immediate: true,
            handler() {
                // TODO: find the cause of the race condition and remove this watcher
                // we had an event where the resizeListener was called before the proper rendering of the parent component
                // resulting in font-size being 0, rechecking required size here to be sure
                this.resizeListener();
            }
        }
    },

    mounted(): void {
        this.interpolateFontSizes();

        const { unsubscriber, state } = this.api.onStateChange(
            (partial: Partial<PlayerState>) => {
                this.updateState(partial);
            }
        );
        this.updateState(state);

        this.api.onSeek(() => {
            // when user seeks, we clear the current subtitles on screen
            this.rawSubtitles = [];
        });

        this.resizeListener = throttle(() => {
            const newVideoWidth = get(this, '$parent.$el.offsetWidth', 300);
            this.fontStyles = this.getFontStyles(newVideoWidth);
        }, 500);

        this.unsubscribers.push(
            unsubscriber,
            addEventListener(window, 'resize', () => this.resizeListener()),
            this.api.onCustomAction((payload, context) => {
                if (context === 'clientFullscreen') {
                    this.resizeListener();
                }
            })
        );
        this.resizeListener();
    },

    beforeDestroy(): void {
        for (const unsubscriber of this.unsubscribers) {
            if (typeof unsubscriber === 'function') {
                unsubscriber();
            }
        }
    },

    methods: {
        updateState(state: Partial<PlayerState>) {
            this.rawSubtitles = get(state, 'subtitles', this.rawSubtitles);

            this.subtitlesEnabled = get(state, 'subtitlesEnabled', this.subtitlesEnabled);
            if (get(state, 'subtitleTrackActive')) {
                // At the moment another language is selected, we clear the current subtitles on screen
                this.rawSubtitles = [];
            }
            if (state.hasOwnProperty('isVideoElementFullScreen')) {
                this.resizeListener();
            }
        },

        getFontStyles(videoWidth:number) {
            for (const item of this.styles) {
                if (videoWidth >= item.minWidth) {
                    console.log('[Subtitle component] switching to style', item.minWidth);
                    return buildStyle(item.style, videoWidth);
                }
            }
            return buildStyle(this.styles[this.styles.length - 1].style, videoWidth);
        },

        interpolateFontSizes() {
            const arrayLength = FONTS_FOR_SCREEN_WIDTH.length;
            this.styles = cloneDeep(FONTS_FOR_SCREEN_WIDTH) as unknown as DynamicFontStyles[];
            // going backward and not checking the first item, assign it later
            for (let i = arrayLength - 1;i > 0;i--) {
                const a = {
                    x: FONTS_FOR_SCREEN_WIDTH[i].minWidth,
                    y: FONTS_FOR_SCREEN_WIDTH[i].style.fontSize
                };
                const b = {
                    x: FONTS_FOR_SCREEN_WIDTH[i - 1].minWidth,
                    y: FONTS_FOR_SCREEN_WIDTH[i - 1].style.fontSize
                };
                this.styles[i].style.fontSize = getLineFunction(a, b);
                a.y = FONTS_FOR_SCREEN_WIDTH[i].style.lineHeight;
                b.y = FONTS_FOR_SCREEN_WIDTH[i - 1].style.lineHeight;
                this.styles[i].style.lineHeight = getLineFunction(a, b);
            }
            // assign first item to same as second item
            this.styles[0].style.fontSize = this.styles[1].style.fontSize;
            this.styles[0].style.lineHeight = this.styles[1].style.lineHeight;
        }
    }
});

const buildStyle = (style:DynamicStyles, width:number) => {
    return {
        ...style,
        fontSize: Math.round(style.fontSize(width)) + 'px',
        lineHeight: Math.round(style.lineHeight(width)) + 'px'
    };
};

const getLineFunction = (a:{x:number, y:number}, b:{x:number, y:number}) => {
    const slope = (b.y - a.y) / (b.x - a.x);
    const offset = a.y - slope * a.x;
    return (x:number) => slope * x + offset;
};

const splitLinesAtLineBreak = (arr: string[]): string[] => {
    const lines = [];
    for (const line of arr) {
        lines.push(...line.split(/\r?\n/).filter(l => l !== ''));
    }
    return lines;
};

